// Single entry point for all client-side analytics. Components and the Inertia
// entrypoint MUST go through this wrapper — never import posthog-js directly.
//
// Design (per the analytics overhaul plan):
//   * Dormant by default. The PostHog key/host are read at RUNTIME from
//     <meta name="posthog-key"> / <meta name="posthog-host"> tags the server
//     renders ONLY when POSTHOG_PROJECT_API_KEY is set — so prod activates while
//     staging/dev stay dark, with no per-deploy key juggling and nothing baked
//     into the build. posthog-js is *dynamically* imported only when that key is
//     present, so with no key it is never even downloaded — zero bundle cost.
//   * BROAD product capture is ON: autocapture (every click/submit), pageviews,
//     page-leave/dwell, dead-click + rageclick frustration, heatmaps, AND session
//     replay — so we see how people actually use the app, not just hand-picked
//     events. PRIVACY (founder decision 2026-10-05: replay shows layout + clicks,
//     never words): autocapture event text + element attributes are masked;
//     replay masks EVERY rendered text node app-wide plus every input value,
//     draws every <img> as an empty box (avatars carry names in alt/title and
//     faces in src; rating faces reveal scores), never ships network
//     headers/bodies or console logs (hard client-side overrides, whatever the
//     PostHog project settings say), and strips query strings from recorded
//     request URLs. PostHog exception autocapture is OFF (browser errors go to
//     Sentry via lib/errorReporting.ts, which scrubs them). Regions that carry
//     AI conversation content (and the Admin AI pages) are BLOCKED with the
//     `ph-no-capture` class — replay draws an empty box, autocapture ignores
//     clicks inside. Replay records element ATTRIBUTES verbatim (posthog-js
//     1.380 has no attribute-masking hook), so any element whose title /
//     aria-label / alt / placeholder carries a name or free text must sit in a
//     blocked region, or carry `data-replay-block` (replay-only block;
//     autocapture unaffected). The Inertia initial page JSON is in a <script>
//     element, whose contents replay never records
//     (config/initializers/inertia_rails.rb). Whole conversations stay
//     reviewable through SQL / Admin AI Review, so nothing is lost by keeping
//     them out of replay. Surveys stay off. Our explicit named events still
//     fire for precise funnels.
//   * IDENTIFIED to the signed-in user using the SAME id the server-side mirror
//     uses as PostHog distinct_id (current_user.id, surfaced via a meta tag), so
//     browser and server events resolve to one person — otherwise the SDK keeps
//     an anonymous id and every cross-source funnel splits in two.
//   * A client-side flood guard (dedupe + rolling-window cap) means a buggy
//     render loop can't fire thousands of events at PostHog.
//   * A kill switch (VITE_ANALYTICS_DISABLED=true) hard-disables emission while
//     leaving the API intact.

type Props = Record<string, unknown>;
type PostHog = (typeof import('posthog-js'))['default'];

const KILLED = import.meta.env.VITE_ANALYTICS_DISABLED === 'true';

// Key/host come from server-rendered meta tags (runtime), present only where
// POSTHOG_PROJECT_API_KEY is configured. One shared build is correct everywhere.
function metaContent(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return (
    document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ||
    undefined
  );
}

// Path-only form of a recorded request URL (see maskCapturedNetworkRequestFn).
function stripQueryAndHash(url: string): string {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

let ph: PostHog | null = null;
let ready = false;
let initializing: Promise<void> | null = null;

// --- Flood guard -----------------------------------------------------------
const DEDUPE_MS = 1000; // drop identical event+props fired within this window
const WINDOW_MS = 10_000; // rolling window for the global cap
const MAX_PER_WINDOW = 100; // hard cap on events emitted per window
const recent = new Map<string, number>();
let windowStart = 0;
let windowCount = 0;

function stableSignature(event: string, props: Props): string {
  try {
    return `${event}|${JSON.stringify(props, Object.keys(props).sort())}`;
  } catch {
    return event;
  }
}

function allow(event: string, props: Props): boolean {
  const now =
    typeof performance !== 'undefined' ? performance.now() : Date.now();

  const sig = stableSignature(event, props);
  const last = recent.get(sig);
  if (last !== undefined && now - last < DEDUPE_MS) return false;
  recent.set(sig, now);
  if (recent.size > 500) recent.clear(); // bound memory

  if (now - windowStart > WINDOW_MS) {
    windowStart = now;
    windowCount = 0;
  }
  windowCount += 1;
  return windowCount <= MAX_PER_WINDOW;
}

// --- Public API ------------------------------------------------------------

/** Boot PostHog if (and only if) a key is configured and we're not killed. */
export function initAnalytics(): Promise<void> {
  const key = metaContent('posthog-key');
  const host = metaContent('posthog-host') ?? 'https://us.i.posthog.com';
  // The signed-in user's id — the SAME value the server-side mirror uses as the
  // PostHog distinct_id (AnalyticsService -> current_user.id). Rendered into a
  // meta tag only when a user is present. We identify the browser SDK with it so
  // client-side and server-side events resolve to one person, not two.
  const distinctId = metaContent('posthog-distinct-id');
  // Org context for breakdowns + cohorts — the SAME values the server envelope
  // uses (company_id = employee.company_id; role = manager|member). company_id
  // distinguishes customer orgs in PostHog; role splits manager vs member.
  const companyId = metaContent('posthog-company-id');
  const role = metaContent('posthog-role');
  if (ready || !key || KILLED) return Promise.resolve();
  if (initializing) return initializing;

  initializing = import('posthog-js')
    .then(mod => {
      ph = mod.default;
      ph.init(key, {
        api_host: host,
        autocapture: true,
        // 'history_change' (not true): this is an Inertia SPA, so most route
        // changes are History API pushStates with no full reload. true captures
        // only the initial load; 'history_change' also fires a pageview on each
        // client-side navigation (and pairs $pageleave for dwell).
        capture_pageview: 'history_change',
        capture_pageleave: true,
        capture_dead_clicks: true,
        rageclick: true,
        // Heatmaps are a SEPARATE collection path from autocapture — off unless
        // forced here (otherwise gated by PostHog project-side remote config, so
        // autocapture:true alone yields no $heatmap_data). Turn them on at the
        // source so heatmap data ships regardless of the project default.
        capture_heatmaps: true,
        disable_surveys: true,
        // Session replay ON for layout + clicks only — no words (founder,
        // 2026-10-05). Each option below is a CLIENT-side value that wins over
        // the PostHog project's remote settings (verified in posthog-js 1.380.1
        // lib/src/extensions/replay/external/lazy-loaded-session-recorder.js
        // `_masking` / `_isConsoleLogCaptureEnabled`, and external/config.js
        // `buildNetworkRequestOptions` "client can always disable").
        disable_session_recording: false,
        session_recording: {
          // Every typed value (inputs, textareas, selects) is recorded as ***.
          maskAllInputs: true,
          // Blocked elements are drawn as an empty box of the same size: rrweb
          // keeps only their class + geometry, never other attributes or
          // children. <img>: avatar alt/title carry employee names, src is a
          // face. [data-replay-block]: a single element whose attributes carry
          // a name or free text outside a `ph-no-capture` region (that class
          // stays the default blockClass alongside this selector).
          blockSelector: 'img, [data-replay-block]',
          // '*' matches every element, so rrweb masks every text node — in
          // the initial snapshot AND in later mutations (it checks ancestors
          // with closest()), including React portals (dialogs, menus,
          // tooltips) since they are ordinary elements under <body>.
          maskTextSelector: '*',
          // Network capture can never include headers or bodies; `false` here
          // overrides a project-level "record body/headers" toggle.
          recordHeaders: false,
          recordBody: false,
          // If the project ever turns on network timing, recorded request URLs
          // keep their path only: query strings can carry free text (e.g. AI
          // history search `?q=`). Bodies/headers are dropped again here as a
          // second lock.
          maskCapturedNetworkRequestFn: request => ({
            ...request,
            name: stripQueryAndHash(request.name),
            requestHeaders: undefined,
            requestBody: undefined,
            responseHeaders: undefined,
            responseBody: undefined,
          }),
        },
        // Console output can include error text built from user content; never
        // ship it with a replay (overrides the project's console-log toggle).
        enable_recording_console_log: false,
        // No PostHog exception autocapture: an explicit false wins over the
        // project's remote "autocapture exceptions" toggle (posthog-js 1.380.1
        // lib/src/extensions/exception-autocapture/index.js `_requiredConfig`
        // only consults the remote flag when this is undefined). Browser
        // errors go to Sentry through lib/errorReporting.ts, which scrubs them.
        capture_exceptions: false,
        // Mask free text + attribute values in AUTOCAPTURE events so employee
        // content doesn't ride along as event properties; elements stay
        // identified by structure plus our own data-ph-capture-attribute-*
        // tokens (those are read separately and survive this mask).
        mask_all_text: true,
        mask_all_element_attributes: true,
        // Start a fresh session already attributed to the signed-in user, so the
        // very first pageview/autocapture has no anonymous window before identify.
        bootstrap: distinctId
          ? { distinctID: distinctId, isIdentifiedID: true }
          : undefined,
      });
      ready = true;
      // Unify with the server mirror (which keys events by current_user.id).
      // bootstrap covers fresh sessions; this covers a browser still holding a
      // prior/anonymous identity (a different login, or events captured before
      // this identify wiring shipped). identify() won't switch an already-set
      // id, so reset() first. Runs before the initial pageview (which PostHog
      // defers via setTimeout), so that event lands under the correct user too.
      if (distinctId && ph.get_distinct_id() !== distinctId) {
        ph.reset();
        ph.identify(distinctId);
      }
      // Org context: register() attaches company_id/role to EVERY event as
      // super-properties (event-time accurate); setPersonProperties makes the
      // same available for PostHog cohorts / person-level filtering. Skipped when
      // absent (logged-out / no employee).
      const orgProps: Props = {};
      // company_id must be a NUMBER to match the server envelope (AnalyticsService
      // writes the numeric id). A string "9" vs number 9 would split one company
      // across two values in PostHog breakdowns that mix client + server events.
      if (companyId) {
        const companyNum = Number(companyId);
        if (Number.isFinite(companyNum)) orgProps.company_id = companyNum;
      }
      if (role) orgProps.role = role;
      if (Object.keys(orgProps).length > 0) {
        ph.register(orgProps);
        ph.setPersonProperties(orgProps);
      }
    })
    .catch(err => {
      // A failed analytics boot must never affect the app.
      console.warn('[analytics] failed to initialize PostHog', err);
    });

  return initializing;
}

export function isAnalyticsReady(): boolean {
  return ready;
}

/** Associate subsequent events with a stable user id. */
export function identifyUser(
  distinctId: string | number | null | undefined,
  props?: Props
): void {
  if (!ready || !ph || distinctId == null) return;
  ph.identify(String(distinctId), props);
}

export function resetAnalytics(): void {
  if (!ready || !ph) return;
  ph.reset();
}

/**
 * Emit a single explicit, tagged event. High-volume UX events go straight to
 * PostHog through here. No-ops when analytics is dormant/killed or the flood
 * guard trips.
 */
export function track(event: string, props: Props = {}): void {
  if (!ready || !ph || KILLED) return;
  if (!allow(event, props)) return;
  ph.capture(event, props);
}

// --- First-party server-side ingestion -------------------------------------
// Sends a low-volume, registry-declared (source: client) event to the governed
// Rails endpoint (/analytics/events), where it is allowlisted + enriched and
// stored in Postgres for the admin dashboard. Subject to the same flood guard.
// Fire-and-forget; failures never affect the app. `keepalive` lets it survive a
// page unload.
export function trackServer(event: string, props: Props = {}): void {
  if (KILLED) return;
  if (!allow(event, props)) return;

  const token = csrfToken();
  void fetch('/analytics/events', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': token ?? '',
    },
    credentials: 'same-origin',
    keepalive: true,
    body: JSON.stringify({ events: [{ name: event, properties: props }] }),
  }).catch(() => {
    // Ignore — analytics must never surface an error to the user.
  });
}

function csrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('meta[name="csrf-token"]');
  return el?.getAttribute('content') ?? null;
}
